import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
  Card, PageHeader, StatusPill, Btn, Field, inputCls, CheckboxGroup, RadioGroupPills, Empty,
} from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const platforms = ["Instagram", "Facebook", "TikTok", "LinkedIn"];
const ctas = ["Book now", "Reserve now", "Visit us", "Shop now", "Learn more", "Send us a message", "Call us", "Visit website"];
const empty = {
  name: "", description: "", start_date: "", end_date: "", price: "", discount: "",
  promo_code: "", url: "", cta: "", platforms: [], urgency: "Normal", notes: "",
};

export default function Promotions() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/promotions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const n = {};
    if (!form.name.trim()) n.name = "Please enter a promotion name.";
    if (!form.description.trim()) n.description = "Please describe the promotion.";
    if (!form.start_date) n.start_date = "Please choose a start date.";
    setErrors(n);
    if (Object.keys(n).length) return;

    setBusy(true);
    try {
      await api.post("/promotions", form);
      toast.success("Promotion submitted.");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/promotions/${id}`);
      toast.success("Promotion removed.");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="promotions-page">
      <PageHeader
        title="Add a New Promotion"
        body="Send us offers, events or announcements at any time and we'll factor them into your content."
      />

      <form onSubmit={submit} noValidate data-testid="promotion-form">
        <Card>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Promotion Name *" error={errors.name} htmlFor="promo-name">
              <input id="promo-name" data-testid="promotion-name-input" className={inputCls} value={form.name} onChange={set("name")} />
            </Field>
            <Field label="Landing Page URL" htmlFor="promo-url">
              <input id="promo-url" data-testid="promotion-url-input" className={inputCls} value={form.url} onChange={set("url")} />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Description *" error={errors.description} htmlFor="promo-description">
              <textarea id="promo-description" rows={4} data-testid="promotion-description-input" className={inputCls} value={form.description} onChange={set("description")} />
            </Field>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Start Date *" error={errors.start_date} htmlFor="promo-start">
              <input id="promo-start" type="date" data-testid="promotion-start-input" className={inputCls} value={form.start_date} onChange={set("start_date")} />
            </Field>
            <Field label="End Date" htmlFor="promo-end">
              <input id="promo-end" type="date" data-testid="promotion-end-input" className={inputCls} value={form.end_date} onChange={set("end_date")} />
            </Field>
            <Field label="Price" htmlFor="promo-price">
              <input id="promo-price" data-testid="promotion-price-input" className={inputCls} value={form.price} onChange={set("price")} placeholder="€39.95" />
            </Field>
            <Field label="Discount" htmlFor="promo-discount">
              <input id="promo-discount" data-testid="promotion-discount-input" className={inputCls} value={form.discount} onChange={set("discount")} placeholder="20%" />
            </Field>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <Field label="Promo Code" htmlFor="promo-code">
              <input id="promo-code" data-testid="promotion-code-input" className={inputCls} value={form.promo_code} onChange={set("promo_code")} />
            </Field>
            <Field label="Call To Action">
              <select data-testid="promotion-cta-select" className={inputCls} value={form.cta} onChange={set("cta")}>
                <option value="">Choose a CTA</option>
                {ctas.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Platforms">
              <CheckboxGroup
                name="promotion-platforms"
                columns={4}
                options={platforms}
                value={form.platforms}
                onChange={(v) => setForm((f) => ({ ...f, platforms: v }))}
              />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Urgency">
              <RadioGroupPills
                name="promotion-urgency"
                options={["Normal", "Time Sensitive"]}
                value={form.urgency}
                onChange={(v) => setForm((f) => ({ ...f, urgency: v }))}
              />
            </Field>
          </div>

          <div className="mt-5">
            <Field
              label="Additional Instructions"
              hint="Upload related images on the Upload Media page and mention their names here."
              htmlFor="promo-notes"
            >
              <textarea id="promo-notes" rows={3} data-testid="promotion-notes-input" className={inputCls} value={form.notes} onChange={set("notes")} />
            </Field>
          </div>

          <Btn type="submit" testId="promotion-submit-button" className="mt-6" disabled={busy}>
            {busy ? "Submitting…" : "Submit Promotion"}
          </Btn>
        </Card>
      </form>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Your promotions</p>
        {rows.length === 0 ? (
          <Empty testId="promotions-empty" title="No promotions yet" body="Submit your first offer or event above." />
        ) : (
          <div className="space-y-3" data-testid="promotions-list">
            {rows.map((p) => (
              <Card key={p.id} testId={`promotion-row-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-base font-semibold text-slate-900">{p.name}</p>
                    <p className="mt-1 text-sm text-slate-600">{p.description}</p>
                    <p className="mt-2 text-xs text-slate-500">
                      {p.start_date || "—"} → {p.end_date || "open"} · {p.urgency}
                      {p.price ? ` · ${p.price}` : ""}{p.discount ? ` · ${p.discount}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={p.status} />
                    <Btn variant="danger" testId={`promotion-delete-${p.id}`} onClick={() => remove(p.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Btn>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
